import { AxiosError } from 'axios';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import { useBranchContext } from 'providers/BranchProvider';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Dropdown, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { TPurchaseOrder, TPurchaseOrderItem, TPurchaseOrderImage } from 'utils/types';
import AddPurchaseOrderItemForm from './AddPurchaseOrderItemForm';
import UploadPurchaseOrderListImageForm, {
  UploadPurchaseOrderListImageFormHandle
} from './UploadPurchaseOrderListImageForm';
import {
  PurchaseOrderItemFormHandle,
  PurchaseOrderItemSubmitResult
} from './CreatePurchaseOrderForm';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faBan,
  faBoxesStacked,
  faColumns,
  faExclamationTriangle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import EditPurchaseOrderItemForm, {
  EditPurchaseOrderItemFormProps
} from './EditPurchaseOrderItemForm';
import PurchaseOrderActions from './PurchaseOrderActions';
import SupplierName from './SupplierName';
import BadgeStatus from 'utils/BadgeStatus';
import Badge from 'components/base/Badge';
import ProductMovementBadge from 'components/shared/common/ProductMovementBadge';
import OrderingOpinionBadge from 'components/shared/common/OrderingOpinionBadge';
import ProductFitmentPreview from 'components/shared/common/ProductFitmentPreview';
import ProgressBadge from 'components/common/ProgressBadge';
import useDebounce from 'hooks/useDebounce';
import { formatLongIsoDate, formatLongIsoDateOrDash } from 'utils/formatLongIsoDate';
import {
  DocumentPreview,
  parseDocumentPreviewResponse,
  revokeDocumentPreview
} from 'utils/printHtmlAsPdf';
import HtmlPdfPreviewModal from 'components/modals/HtmlPdfPreviewModal';
import ImageModal, {
  ImageModalHandle,
  ImageModalProps
} from 'components/modals/ImageModal';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';
import EstimatedUnitPriceCell, {
  formatPeso
} from './EstimatedUnitPriceCell';
import RemarksCell from './RemarksCell';
import CancelCommentForm, {
  CancelCommentFormHandle
} from './CancelCommentForm';
import MovePurchaseOrderItemForm, {
  MovePurchaseOrderItemFormHandle
} from './MovePurchaseOrderItemForm';
import BindPurchaseOrderItemToInvoiceForm, {
  BindPurchaseOrderItemToInvoiceFormHandle
} from './BindPurchaseOrderItemToInvoiceForm';
import ProcurementLimitWarning from '../ProcurementLimitWarning';
import CreateProductFromUnencodedModal from 'components/modals/CreateProductFromUnencodedModal';

const normalizeSubmitResult = (
  result: PurchaseOrderItemSubmitResult | undefined
) => {
  if (result === true) {
    return { success: true as const };
  }
  if (result === false || result == null) {
    return { success: false as const };
  }
  return result;
};

const isProductMatchQueued = (
  status: string | number | undefined | null
) => status === 'Queued' || status === 10;

type ConfigurableColumnId =
  | 'suggestion'
  | 'quantity'
  | 'estimatedUnitPrice'
  | 'estimatedAmount'
  | 'existingQty'
  | 'orderingOpinion'
  | 'lastQuantityReceived'
  | 'lastDeliveryReceivedOn'
  | 'orderByUserName'
  | 'remarks'
  | 'fulfillmentStatus';

const ALWAYS_VISIBLE_COLUMN_IDS = new Set(['productName', 'actions']);

const COLUMN_VISIBILITY_STORAGE_KEY = 'purchaseOrderDetails.visibleColumns';
const MOBILE_COLUMN_VISIBILITY_STORAGE_KEY =
  'purchaseOrderDetails.visibleColumns.mobile';
const PHONE_MAX_WIDTH = 768;

const CONFIGURABLE_COLUMNS: {
  id: ConfigurableColumnId;
  label: string;
  defaultVisible: boolean;
}[] = [
  { id: 'suggestion', label: 'Product Match', defaultVisible: true },
  { id: 'quantity', label: 'Quantity', defaultVisible: true },
  { id: 'estimatedUnitPrice', label: 'Est. Unit Price', defaultVisible: true },
  { id: 'estimatedAmount', label: 'Est. Amount', defaultVisible: true },
  { id: 'existingQty', label: 'Existing Qty', defaultVisible: true },
  { id: 'orderingOpinion', label: 'AI Opinion', defaultVisible: true },
  { id: 'lastQuantityReceived', label: 'Last Qty Received', defaultVisible: true },
  {
    id: 'lastDeliveryReceivedOn',
    label: 'Last Delivery Received',
    defaultVisible: true
  },
  { id: 'orderByUserName', label: 'Ordered By', defaultVisible: true },
  { id: 'remarks', label: 'Comment', defaultVisible: true },
  { id: 'fulfillmentStatus', label: 'Fulfillment Status', defaultVisible: true }
];

const DEFAULT_VISIBLE_COLUMNS = CONFIGURABLE_COLUMNS.filter(
  column => column.defaultVisible
).map(column => column.id);

const MOBILE_DEFAULT_VISIBLE_COLUMNS: ConfigurableColumnId[] = ['quantity'];

const isPhoneViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH;

const loadVisibleColumnsFor = (isPhone: boolean): ConfigurableColumnId[] => {
  const defaults = isPhone
    ? MOBILE_DEFAULT_VISIBLE_COLUMNS
    : DEFAULT_VISIBLE_COLUMNS;
  const storageKey = isPhone
    ? MOBILE_COLUMN_VISIBILITY_STORAGE_KEY
    : COLUMN_VISIBILITY_STORAGE_KEY;

  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return defaults;
    }

    const parsed = JSON.parse(stored) as string[];
    if (!Array.isArray(parsed)) {
      return defaults;
    }

    const validIds = new Set(CONFIGURABLE_COLUMNS.map(column => column.id));
    const visibleColumns = parsed.filter((id): id is ConfigurableColumnId =>
      validIds.has(id as ConfigurableColumnId)
    );

    return visibleColumns.length > 0 ? visibleColumns : defaults;
  } catch {
    return defaults;
  }
};

const loadVisibleColumns = (): ConfigurableColumnId[] =>
  loadVisibleColumnsFor(isPhoneViewport());

const PurchaseOrderDetails = () => {
  const { id: purchaseOrderId } = useParams();
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const { breakpoints } = useBreakpoints();
  const isPhone = breakpoints?.down?.('md') ?? isPhoneViewport();
  const canApprove = hasAnyRole(token?.roles, FeatureRoles.purchaseOrderApprove);
  const canDelete = hasAnyRole(token?.roles, FeatureRoles.purchaseOrderDelete);
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const canMarkFulfilled = hasAnyRole(
    token?.roles,
    FeatureRoles.purchaseOrderFulfillment
  );
  //   const branchDomain = useMemo(() => selectedBranch?.branchDomain, [selectedBranch]);
  const [purchaseOrderDetails, setPurchaseOrderDetails] =
    useState<TPurchaseOrder>();
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<
    TPurchaseOrderItem[]
  >([]);
  const [isLoading, setIsloading] = useState(false);
  const [isQueueingAiJob, setIsQueueingAiJob] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<DocumentPreview | null>(null);
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const [createFromUnencodedItem, setCreateFromUnencodedItem] =
    useState<TPurchaseOrderItem | null>(null);
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const imageModalRef = useRef<ImageModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const [imageModalProps, setImageModalProps] = useState<ImageModalProps>();
  const addPurchaseOrderItemFormRef = useRef<PurchaseOrderItemFormHandle>(null);
  const editPurchaseOrderItemFormRef =
    useRef<PurchaseOrderItemFormHandle>(null);
  const uploadListImageFormRef =
    useRef<UploadPurchaseOrderListImageFormHandle>(null);
  const movePurchaseOrderItemFormRef =
    useRef<MovePurchaseOrderItemFormHandle>(null);
  const bindInvoiceFormRef =
    useRef<BindPurchaseOrderItemToInvoiceFormHandle>(null);
  const cancelCommentFormRef = useRef<CancelCommentFormHandle>(null);
  const [pollForMatchesUntil, setPollForMatchesUntil] = useState<number | null>(
    null
  );
  const [processingSuggestionItemId, setProcessingSuggestionItemId] = useState<
    number | null
  >(null);
  const [matchingItemIds, setMatchingItemIds] = useState<number[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<ConfigurableColumnId[]>(
    loadVisibleColumns
  );
  const columnVisibilityStorageKey = isPhone
    ? MOBILE_COLUMN_VISIBILITY_STORAGE_KEY
    : COLUMN_VISIBILITY_STORAGE_KEY;
  const defaultVisibleColumns = isPhone
    ? MOBILE_DEFAULT_VISIBLE_COLUMNS
    : DEFAULT_VISIBLE_COLUMNS;
  const navigate = useNavigate();
  const debouncedBranchDomain = useDebounce(selectedBranch?.branchDomain, 300);

  const hasPendingSuggestions = useMemo(
    () =>
      purchaseOrderItems.some(item => item.suggestionStatus === 'Pending'),
    [purchaseOrderItems]
  );

  const hasQueuedProductMatchesOnPage = useMemo(
    () =>
      matchingItemIds.length > 0 ||
      purchaseOrderItems.some(item =>
        isProductMatchQueued(item.productMatchStatus)
      ),
    [purchaseOrderItems, matchingItemIds]
  );

  // Prefer PO-level flag so header badges stay visible while items refresh.
  const hasQueuedProductMatches =
    purchaseOrderDetails?.hasQueuedProductMatches === true ||
    hasQueuedProductMatchesOnPage;

  const isAnalyzing = purchaseOrderDetails?.status === 'Analyzing';
  const canEditEstimate =
    isAdmin ||
    (purchaseOrderDetails?.status !== 'Closed' &&
      purchaseOrderDetails?.status !== 'Cancelled');
  const canEditRemarks = canEditEstimate;
  const canCancelLine =
    purchaseOrderDetails?.status === 'Approved' ||
    purchaseOrderDetails?.status === 'Sent';
  const canMarkNoStock = canMarkFulfilled && canCancelLine;
  const canMoveLine =
    purchaseOrderDetails?.status === 'New' ||
    purchaseOrderDetails?.status === 'Future' ||
    purchaseOrderDetails?.status === 'Approved' ||
    purchaseOrderDetails?.status === 'Sent';
  const canMoveToResupply =
    purchaseOrderDetails?.status !== 'Closed' &&
    purchaseOrderDetails?.status !== 'Cancelled' &&
    purchaseOrderDetails?.status !== 'ForDeletion';
  const canRelocateNoStock =
    purchaseOrderDetails?.status === 'New' ||
    purchaseOrderDetails?.status === 'Future' ||
    purchaseOrderDetails?.status === 'Approved' ||
    purchaseOrderDetails?.status === 'Sent' ||
    purchaseOrderDetails?.status === 'Closed' ||
    purchaseOrderDetails?.status === 'Cancelled';
  const isActionableNoStockItem = (item: TPurchaseOrderItem) =>
    item.isNoStock === true && !item.movedToPurchaseOrderId;
  const canBindToInvoice =
    purchaseOrderDetails?.status !== 'Cancelled' &&
    purchaseOrderDetails?.status !== 'ForDeletion';

  const isOrderingOpinionRunning = useMemo(() => {
    const status = purchaseOrderDetails?.orderingOpinionStatus;
    return (
      isAnalyzing ||
      status === 'Pending' ||
      status === 10 ||
      status === 'Processing' ||
      status === 20
    );
  }, [purchaseOrderDetails?.orderingOpinionStatus, isAnalyzing]);

  const hasImagesExtracting = useMemo(
    () =>
      (purchaseOrderDetails?.images ?? []).some(
        image =>
          image.extractionStatus === 'Pending' ||
          image.extractionStatus === 0 ||
          image.extractionStatus === 'Processing' ||
          image.extractionStatus === 10
      ),
    [purchaseOrderDetails?.images]
  );

  const hasBackgroundTaskInProgress =
    isAnalyzing ||
    purchaseOrderDetails?.hasBackgroundTaskInProgress === true ||
    hasImagesExtracting ||
    hasQueuedProductMatches ||
    isOrderingOpinionRunning;

  const startMatchPolling = (durationMs = 30000) => {
    setPollForMatchesUntil(Date.now() + durationMs);
  };

  const fetchSupplierDetails = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) {
      setIsloading(true);
    }
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}`
      );
      if (res.data?.isPending || res.data?.supplierId == null) {
        navigate('/purchase-order/pending', { replace: true });
        return;
      }
      setPurchaseOrderDetails(res.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          console.log(error.response?.data.errorMessage);
          console.error(error.response?.data.errorMessage);
        }
      }
    } finally {
      if (!silent) {
        setIsloading(false);
      }
    }
  }, [debouncedBranchDomain, purchaseOrderId, navigate]);

  const fetchPurchaseOrderItems = async () => {
    if (selectedBranch?.branchDomain === undefined) {
      return false;
    }
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items?page=1&pageSize=5000`
      );
      setPurchaseOrderItems(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const bindCreatedProductToPurchaseOrderItem = async (productId: number) => {
    const item = createFromUnencodedItem;
    if (!item || !selectedBranch?.branchDomain || !purchaseOrderId) {
      throw new Error('Missing purchase order item');
    }

    await axiosInstance.put(
      `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${item.purchaseOrderItemId}`,
      {
        productId,
        productName: '',
        quantity: item.quantity,
        unitOfMeasure: item.unitOfMeasure,
        remarks: item.remarks,
        allowRecentlyOrdered: true,
        allowNonCriticalQuantity: true,
        allowUnsupportedBySupplier: true
      }
    );
    await fetchPurchaseOrderItems();
  };

  const handleDeletePurchaseOrderItem = async (purchaseOrderItemId: number) => {
    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}`
      );
      toast.success('Item Deleted', { ...toastConfig, autoClose: 2000 });
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
    modalRef.current.hide();
  };

  const handleEstimatedPriceUpdated = (updatedItem: TPurchaseOrderItem) => {
    setPurchaseOrderItems(prev =>
      prev.map(item =>
        item.purchaseOrderItemId === updatedItem.purchaseOrderItemId
          ? { ...item, ...updatedItem }
          : item
      )
    );
    void fetchSupplierDetails({ silent: true });
  };

  const markProductMatchInProgress = (purchaseOrderItemId?: number) => {
    if (purchaseOrderItemId != null) {
      setMatchingItemIds(prev =>
        prev.includes(purchaseOrderItemId)
          ? prev
          : [...prev, purchaseOrderItemId]
      );
      setPurchaseOrderItems(prev =>
        prev.map(item =>
          item.purchaseOrderItemId === purchaseOrderItemId
            ? {
                ...item,
                productMatchStatus: 'Queued',
                suggestionStatus: 'None'
              }
            : item
        )
      );
    }
    setPurchaseOrderDetails(prev =>
      prev
        ? {
            ...prev,
            hasQueuedProductMatches: true,
            hasBackgroundTaskInProgress: true
          }
        : prev
    );
  };

  const handleSubmitItem = async () => {
    if (addPurchaseOrderItemFormRef.current) {
      try {
        const result = normalizeSubmitResult(
          await addPurchaseOrderItemFormRef.current.submit()
        );
        if (result.success) {
          if (result.queuedProductMatch) {
            markProductMatchInProgress();
          }
          await Promise.all([
            fetchPurchaseOrderItems(),
            fetchSupplierDetails({ silent: true })
          ]);
          startMatchPolling();
          modalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSubmitEditedItem = async () => {
    if (editPurchaseOrderItemFormRef.current) {
      try {
        const result = normalizeSubmitResult(
          await editPurchaseOrderItemFormRef.current.submit()
        );
        if (result.success) {
          if (result.queuedProductMatch) {
            markProductMatchInProgress(result.purchaseOrderItemId);
          }
          await Promise.all([
            fetchPurchaseOrderItems(),
            fetchSupplierDetails({ silent: true })
          ]);
          startMatchPolling();
          modalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const addPurchaseOrderItem = () => {
    modalRef.current.show();
    setModalProps({
      title: 'Add Item',
      content: (
        <AddPurchaseOrderItemForm
          ref={addPurchaseOrderItemFormRef}
          purchaseOrderId={
            purchaseOrderId ? parseInt(purchaseOrderId, 10) : undefined
          }
          onCommitted={async result => {
            if (result.queuedProductMatch) {
              markProductMatchInProgress();
            }
            await Promise.all([
              fetchPurchaseOrderItems(),
              fetchSupplierDetails({ silent: true })
            ]);
            startMatchPolling();
            modalRef.current.hide();
          }}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitItem()
    });
  };

  const uploadListPhoto = () => {
    modalRef.current.show();
    setModalProps({
      title: 'Upload list photo',
      content: (
        <UploadPurchaseOrderListImageForm
          ref={uploadListImageFormRef}
          branchDomain={selectedBranch?.branchDomain}
          purchaseOrderId={purchaseOrderId}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitListPhoto()
    });
  };

  const handleSubmitListPhoto = async () => {
    if (uploadListImageFormRef.current) {
      try {
        const isSuccess = await uploadListImageFormRef.current.submit();
        if (isSuccess) {
          await fetchSupplierDetails();
          fetchPurchaseOrderItems();
          startMatchPolling(120000);
          modalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getExtractionStatusLabel = (
    status: TPurchaseOrderImage['extractionStatus']
  ) => {
    if (status === 'Pending' || status === 0) return 'Pending';
    if (status === 'Processing' || status === 10) return 'Processing';
    if (status === 'Done' || status === 20) return 'Done';
    if (status === 'Failed' || status === 30) return 'Failed';
    return String(status ?? '');
  };

  const getExtractionBadgeBg = (
    status: TPurchaseOrderImage['extractionStatus']
  ) => {
    if (status === 'Done' || status === 20) return 'success';
    if (status === 'Failed' || status === 30) return 'danger';
    if (status === 'Processing' || status === 10) return 'warning';
    return 'secondary';
  };

  const handlePreviewListImage = async (googleFileId: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/images/${googleFileId}`,
        { responseType: 'blob' }
      );
      const imageUrl = URL.createObjectURL(res.data);
      imageModalRef.current.show();
      setImageModalProps({
        image: imageUrl,
        size: 'lg',
        googleFileId,
        caption: 'List photo'
      });
    } catch (error) {
      console.error('Error fetching list image:', error);
      toast.error('Failed to open image', { ...toastConfig, autoClose: 3000 });
    }
  };

  const handleDeleteListImage = async (image: TPurchaseOrderImage) => {
    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/images/${image.googleFileId}`
      );
      toast.success('List photo deleted', { ...toastConfig, autoClose: 2000 });
      await fetchSupplierDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.errorMessage || 'Delete failed', {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const editPurchaseOrderItem = ({
    productId,
    productName,
    quantity,
    unitOfMeasure,
    remarks,
    purchaseOrderItemId
  }: EditPurchaseOrderItemFormProps) => {
    modalRef.current.show();
    setModalProps({
      title: 'Edit Item',
      content: (
        <EditPurchaseOrderItemForm
          ref={editPurchaseOrderItemFormRef}
          productId={productId}
          productName={productName}
          quantity={quantity}
          unitOfMeasure={unitOfMeasure}
          remarks={remarks}
          purchaseOrderId={
            purchaseOrderId ? parseInt(purchaseOrderId, 10) : undefined
          }
          purchaseOrderItemId={purchaseOrderItemId}
          onCommitted={async result => {
            if (result.queuedProductMatch) {
              markProductMatchInProgress(result.purchaseOrderItemId);
            }
            await Promise.all([
              fetchPurchaseOrderItems(),
              fetchSupplierDetails({ silent: true })
            ]);
            startMatchPolling();
            modalRef.current.hide();
          }}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmitEditedItem()
    });
  };

  const markAsNew = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/markAsNew`
      );
      toast.success('Purchase order is now New.', {
        ...toastConfig,
        autoClose: 2000
      });
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const sendForApproval = async () => {
    if (hasPendingSuggestions) {
      toast.error(
        'Resolve product suggestions before sending for approval.',
        { ...toastConfig, autoClose: 3000 }
      );
      return;
    }
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/sendForApproval`
      );
      toast.success('Sent for approval — AI analysis running…', {
        ...toastConfig,
        autoClose: 2000
      });
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
      startMatchPolling(120000);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const queueAiJob = async (
    path: string,
    successMessage: string,
    confirmMessage?: string
  ) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;

    setIsQueueingAiJob(true);
    try {
      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/${path}`
      );
      toast.success(successMessage, {
        ...toastConfig,
        autoClose: 2500
      });
      fetchSupplierDetails({ silent: true });
      fetchPurchaseOrderItems();
      startMatchPolling(120000);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to queue AI job',
          {
            ...toastConfig,
            autoClose: 4000
          }
        );
      }
    } finally {
      setIsQueueingAiJob(false);
    }
  };

  const approveRequest = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/approve`
      );
      toast.success('Request approved', { ...toastConfig, autoClose: 2000 });
      fetchSupplierDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const confirmRequest = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/confirmSent`
      );
      toast.success('Purchase order marked as sent.', {
        ...toastConfig,
        autoClose: 4000
      });
      fetchSupplierDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const cancelPurchaseOrder = () => {
    modalRef.current.show();
    setModalProps({
      title: 'Cancel purchase order',
      content: <CancelCommentForm ref={cancelCommentFormRef} label="Reason" />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleCancelPurchaseOrder(),
      onSubmitCustomDisplay: 'Cancel order'
    });
  };

  const handleCancelPurchaseOrder = async () => {
    const comment = cancelCommentFormRef.current?.getComment() ?? '';
    if (!comment) {
      toast.error('Please add a reason', { ...toastConfig, autoClose: 3000 });
      return;
    }
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/cancel`,
        { comment }
      );
      toast.success('Purchase order cancelled', {
        ...toastConfig,
        autoClose: 2000
      });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const cancelPurchaseOrderItem = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    modalRef.current.show();
    setModalProps({
      title: `Cancel ${productName}`,
      content: <CancelCommentForm ref={cancelCommentFormRef} label="Reason" />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleCancelPurchaseOrderItem(purchaseOrderItemId),
      onSubmitCustomDisplay: 'Cancel item'
    });
  };

  const handleCancelPurchaseOrderItem = async (purchaseOrderItemId: number) => {
    const comment = cancelCommentFormRef.current?.getComment() ?? '';
    if (!comment) {
      toast.error('Please add a reason', { ...toastConfig, autoClose: 3000 });
      return;
    }
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/cancel`,
        { comment }
      );
      toast.success('Item cancelled', { ...toastConfig, autoClose: 2000 });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const movePurchaseOrderItem = (purchaseOrderItemId: number, productName: string) => {
    modalRef.current.show();
    setModalProps({
      title: `Move ${productName}`,
      content: <MovePurchaseOrderItemForm ref={movePurchaseOrderItemFormRef} />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleMovePurchaseOrderItem(purchaseOrderItemId),
      onSubmitCustomDisplay: 'Move item'
    });
  };

  const movePurchaseOrderItemToResupply = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    setModalProps({
      title: 'Move to resupply',
      content: (
        <p className="mb-0">
          Move {productName} to the resupply list? Remaining quantity will be
          added there and this line will be taken off the purchase order.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleMovePurchaseOrderItemToResupply(purchaseOrderItemId);
      },
      onSubmitCustomDisplay: 'Move to resupply'
    });
    modalRef.current.show();
  };

  const bindPurchaseOrderItemToInvoice = (
    purchaseOrderItemId: number,
    productName: string,
    hasExistingInvoice: boolean
  ) => {
    if (!purchaseOrderId) {
      return;
    }

    setModalProps({
      title: hasExistingInvoice
        ? `Change invoice for ${productName}`
        : `Bind ${productName} to invoice`,
      content: (
        <BindPurchaseOrderItemToInvoiceForm
          key={purchaseOrderItemId}
          ref={bindInvoiceFormRef}
          purchaseOrderId={purchaseOrderId}
          purchaseOrderItemId={purchaseOrderItemId}
          hasExistingInvoice={hasExistingInvoice}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleBindPurchaseOrderItemToInvoice(
          purchaseOrderItemId,
          hasExistingInvoice
        );
      },
      onSubmitCustomDisplay: hasExistingInvoice
        ? 'Update invoice'
        : 'Bind invoice'
    });
    modalRef.current.show();
  };

  const handleBindPurchaseOrderItemToInvoice = async (
    purchaseOrderItemId: number,
    hasExistingInvoice: boolean
  ) => {
    if (bindInvoiceFormRef.current?.shouldUnbind()) {
      await handleUnbindPurchaseOrderItemFromInvoice(purchaseOrderItemId);
      return;
    }

    const procurementInvoiceItemId =
      bindInvoiceFormRef.current?.getProcurementInvoiceItemId();
    if (!procurementInvoiceItemId) {
      toast.error('Please select an invoice', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/bindInvoiceItem`,
        { procurementInvoiceItemId }
      );
      toast.success(
        hasExistingInvoice ? 'Invoice updated' : 'Bound to invoice',
        { ...toastConfig, autoClose: 2000 }
      );
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const removePurchaseOrderItemInvoice = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    setModalProps({
      title: `Remove invoice from ${productName}`,
      content: (
        <p className="mb-0">
          Remove the procurement invoice from this purchase order item? The
          line will go back to unfulfilled until another invoice is bound.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleUnbindPurchaseOrderItemFromInvoice(purchaseOrderItemId);
      },
      onSubmitCustomDisplay: 'Remove invoice'
    });
    modalRef.current.show();
  };

  const handleUnbindPurchaseOrderItemFromInvoice = async (
    purchaseOrderItemId: number
  ) => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/unbindInvoiceItem`
      );
      toast.success('Invoice removed', { ...toastConfig, autoClose: 2000 });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const markPurchaseOrderItemFulfilled = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    setModalProps({
      title: `Mark ${productName} as fulfilled`,
      content: (
        <>
          <p className="mb-3">
            Mark this purchase order item as fulfilled without binding a
            procurement invoice? This can be used when the goods were received
            against an unencoded or unmatched invoice line.
          </p>
          <CancelCommentForm
            key={`fulfilled-${purchaseOrderItemId}`}
            ref={cancelCommentFormRef}
            label="Comment"
            placeholder="Why this line is fulfilled"
          />
        </>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleMarkPurchaseOrderItemFulfilled(purchaseOrderItemId);
      },
      onSubmitCustomDisplay: 'Mark as fulfilled'
    });
    modalRef.current.show();
  };

  const handleMarkPurchaseOrderItemFulfilled = async (
    purchaseOrderItemId: number
  ) => {
    const comment = cancelCommentFormRef.current?.getComment() ?? '';
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/markFulfilled`,
        { comment }
      );
      toast.success('Marked as fulfilled', { ...toastConfig, autoClose: 2000 });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const markPurchaseOrderItemNoStock = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    setModalProps({
      title: `No stock for ${productName}`,
      content: (
        <>
          <p className="mb-3">
            Flag this item as no stock at the supplier? It stays on the
            purchase order history and the remaining quantity is moved to
            resupply.
          </p>
          <CancelCommentForm
            key={`nostock-${purchaseOrderItemId}`}
            ref={cancelCommentFormRef}
            label="Comment"
            placeholder="Optional note about the supplier stock"
          />
        </>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleMarkPurchaseOrderItemNoStock(purchaseOrderItemId);
      },
      onSubmitCustomDisplay: 'No stock'
    });
    modalRef.current.show();
  };

  const handleMarkPurchaseOrderItemNoStock = async (
    purchaseOrderItemId: number
  ) => {
    const comment = cancelCommentFormRef.current?.getComment() ?? '';
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/markNoStock`,
        { comment }
      );
      toast.success('Marked as no stock and moved to resupply', {
        ...toastConfig,
        autoClose: 2000
      });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleMarkSelectedItemsNoStock = async (
    purchaseOrderItemIds: number[]
  ) => {
    if (purchaseOrderItemIds.length === 0) {
      toast.error('Select at least one item to mark as no stock', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }
    const comment = cancelCommentFormRef.current?.getComment() ?? '';
    try {
      const res = await axiosInstance.put<{ movedItemCount: number }>(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/markNoStock`,
        { purchaseOrderItemIds, comment }
      );
      const moved = res.data.movedItemCount ?? purchaseOrderItemIds.length;
      toast.success(
        `Marked ${moved} item${moved === 1 ? '' : 's'} as no stock`,
        { ...toastConfig, autoClose: 3000 }
      );
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleMovePurchaseOrderItemToResupply = async (
    purchaseOrderItemId: number
  ) => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/moveToResupply`
      );
      toast.success('Moved to resupply', { ...toastConfig, autoClose: 2000 });
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleMoveSelectedItemsToResupply = async (
    purchaseOrderItemIds: number[]
  ) => {
    if (purchaseOrderItemIds.length === 0) {
      toast.error('Select at least one item to move to resupply', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }
    try {
      const res = await axiosInstance.put<{ movedItemCount: number }>(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/moveToResupply`,
        { purchaseOrderItemIds }
      );
      const moved = res.data.movedItemCount ?? purchaseOrderItemIds.length;
      toast.success(
        `Moved ${moved} item${moved === 1 ? '' : 's'} to resupply`,
        { ...toastConfig, autoClose: 3000 }
      );
      modalRef.current.hide();
      fetchSupplierDetails();
      fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleMovePurchaseOrderItem = async (purchaseOrderItemId: number) => {
    const supplierId = movePurchaseOrderItemFormRef.current?.getSupplierId();
    if (!supplierId) {
      toast.error('Please select a supplier', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }
    try {
      const res = await axiosInstance.put<{ targetPurchaseOrderId: number }>(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/move`,
        { supplierId }
      );
      toast.success(`Moved to PO #${res.data.targetPurchaseOrderId}`, {
        ...toastConfig,
        autoClose: 3000
      });
      modalRef.current.hide();
      navigate(`/purchase-order-details/${res.data.targetPurchaseOrderId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleMoveSelectedItems = async (purchaseOrderItemIds: number[]) => {
    if (purchaseOrderItemIds.length === 0) {
      toast.error('Select at least one item to move', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }
    try {
      const res = await axiosInstance.put<{
        targetPurchaseOrderId: number;
        movedItemCount: number;
        destinationStatus: string;
      }>(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/move`,
        { purchaseOrderItemIds }
      );
      const destinationLabel = `PO #${res.data.targetPurchaseOrderId}`;
      toast.success(
        `Moved ${res.data.movedItemCount} item${
          res.data.movedItemCount === 1 ? '' : 's'
        } to ${destinationLabel}`,
        { ...toastConfig, autoClose: 3000 }
      );
      modalRef.current.hide();
      navigate(`/purchase-order-details/${res.data.targetPurchaseOrderId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const closeOrder = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/close`
      );
      toast.success('Purchase order closed', {
        ...toastConfig,
        autoClose: 3000
      });
      modalRef.current.hide();
      fetchSupplierDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const confirmCloseOrder = () => {
    modalRef.current.show();
    setModalProps({
      title: 'Close purchase order',
      content: (
        <p className="mb-0">Close this purchase order?</p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => closeOrder(),
      onSubmitCustomDisplay: 'Close order'
    });
  };

  const requestForDeletion = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/requestDeletion`
      );
      toast.success('Request for deletion sent', {
        ...toastConfig,
        autoClose: 2000
      });
      fetchSupplierDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleApproveRequestForDeletion = async () => {
    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}`
      );
      toast.success('Purchase Order Deleted', {
        ...toastConfig,
        autoClose: 2000
      });
      navigate('/purchase-order');
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    setPdfPreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
  };

  const handleGeneratePdf = async () => {
    setPdfPreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
    setShowPdfPreview(true);
    setIsGeneratingPdf(true);
    try {
      const response = await axiosInstance.get(
        `api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/download`,
        {
          responseType: 'blob'
        }
      );

      const parsed = await parseDocumentPreviewResponse(
        response,
        'Purchase Order.pdf'
      );
      setPdfPreview(parsed);
    } catch (error) {
      closePdfPreview();
      if (error instanceof AxiosError) {
        const errorData = error.response?.data;
        const errorMessageText =
          typeof errorData === 'string'
            ? errorData
            : errorData instanceof Blob
            ? await errorData.text()
            : await errorData?.text?.();
        try {
          const errorMessageJson = JSON.parse(errorMessageText);
          if (error.response && error.response.status === 400) {
            toast.error(errorMessageJson?.errorMessage, {
              ...toastConfig,
              autoClose: 3000
            });
            return;
          }
        } catch {
          // fall through
        }
      }
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to generate purchase order PDF.',
        {
          ...toastConfig,
          autoClose: 3000
        }
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleAcceptSuggestion = async (purchaseOrderItemId: number) => {
    setProcessingSuggestionItemId(purchaseOrderItemId);
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/acceptSuggestion`
      );
      toast.success('Suggestion accepted', { ...toastConfig, autoClose: 2000 });
      await fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setProcessingSuggestionItemId(null);
    }
  };

  const handleRejectSuggestion = async (purchaseOrderItemId: number) => {
    setProcessingSuggestionItemId(purchaseOrderItemId);
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/rejectSuggestion`
      );
      toast.success('Suggestion rejected', { ...toastConfig, autoClose: 2000 });
      await fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setProcessingSuggestionItemId(null);
    }
  };

  const deletePurchaseOrderItem = (
    purchaseOrderItemId: number,
    productName: string
  ) => {
    modalRef.current.show();
    setModalProps({
      title: 'Delete Item',
      content: <p>Are you sure you want to delete {productName}</p>,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleDeletePurchaseOrderItem(purchaseOrderItemId)
    });
  };

  const handleClearFollowUp = async (purchaseOrderItemId: number) => {
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/clearFollowUp`
      );
      toast.success('Follow-up flag removed', {
        ...toastConfig,
        autoClose: 2000
      });
      modalRef.current.hide();
      await fetchPurchaseOrderItems();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const clearFollowUpFlag = (purchaseOrderItemId: number, productName: string) => {
    modalRef.current.show();
    setModalProps({
      title: 'Remove follow-up flag',
      content: (
        <p>
          Remove the follow-up flag from {productName}? It will be treated as a
          regular order line.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleClearFollowUp(purchaseOrderItemId),
      onSubmitCustomDisplay: 'Remove flag'
    });
  };

  useEffect(() => {
    setVisibleColumns(loadVisibleColumnsFor(isPhone));
  }, [isPhone]);

  const toggleColumnVisibility = (columnId: ConfigurableColumnId) => {
    setVisibleColumns(prev => {
      const next = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];

      if (next.length === 0) {
        return prev;
      }

      localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(next));
      return next;
    });
  };

  const resetColumnVisibility = () => {
    setVisibleColumns(defaultVisibleColumns);
    localStorage.setItem(
      columnVisibilityStorageKey,
      JSON.stringify(defaultVisibleColumns)
    );
  };

  const allColumns: ColumnDef<TPurchaseOrderItem>[] = [
    {
      id: 'productName',
      accessorKey: 'productName',
      header: 'Product Name',
      enableSorting: false,
      meta: {
        cellProps: { className: 'text-wrap', style: { minWidth: '10rem' } }
      },
      cell: ({ row: { original } }) => {
        const followUpBadge = original.isFollowUp ? (
          <Badge
            bg="warning"
            text="dark"
            className="fs-10"
            role="button"
            title="Remove follow-up flag"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              clearFollowUpFlag(
                original.purchaseOrderItemId,
                original.productName || 'this item'
              )
            }
          >
            For follow up
          </Badge>
        ) : null;

        const requestedBadge = original.isRequested ? (
          <Badge bg="primary" className="fs-10">
            Requested
          </Badge>
        ) : null;

        const recentPoIds = original.recentPurchaseOrderIds ?? [];
        const recentlyOrderedWarning = original.isRecentlyOrdered ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`recent-po-${original.purchaseOrderItemId}`}>
                Recently ordered
                {recentPoIds.length > 0
                  ? ` on PO #${recentPoIds.join(', #')}`
                  : ' in the last 3 purchase orders'}
              </Tooltip>
            }
          >
            <span className="d-inline-flex">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-warning"
                style={{ cursor: 'help' }}
              />
            </span>
          </OverlayTrigger>
        ) : null;

        if (original.productId) {
          return (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <Link
                  to={`/product/${encodeURIComponent(
                    selectedBranch?.branchDomain as string
                  )}/${original.productId}`}
                  className="text-decoration-none fw-bold fs-9"
                >
                  {original.productName
                    ? original.productName
                    : `product ${original.productId}`}
                </Link>
                <ProductMovementBadge
                  movementClass={original.movementClass}
                  netQuantitySoldLast90Days={original.netQuantitySoldLast90Days}
                  distinctSaleDaysLast90Days={
                    original.distinctSaleDaysLast90Days
                  }
                  lastSoldOn={original.lastSoldOn}
                  firstDeliveredOn={original.firstDeliveredOn}
                  tooltipId={`po-movement-${original.purchaseOrderItemId}`}
                />
                {recentlyOrderedWarning}
              </div>
              {(followUpBadge || requestedBadge) && (
                <div className="d-flex flex-wrap gap-1">
                  {followUpBadge}
                  {requestedBadge}
                </div>
              )}
              <ProductFitmentPreview fitments={original.productFitments} />
            </div>
          );
        }
        return (
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2">
              <span className="fs-9">
                {original.productName || 'Unnamed product'}
              </span>
              {recentlyOrderedWarning}
            </div>
            {(followUpBadge || requestedBadge) && (
              <div className="d-flex flex-wrap gap-1">
                {followUpBadge}
                {requestedBadge}
              </div>
            )}
            <ProductFitmentPreview fitments={original.productFitments} />
          </div>
        );
      }
    },
    {
      id: 'suggestion',
      header: 'Product Match',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isQueuedMatch =
          isProductMatchQueued(original.productMatchStatus) ||
          matchingItemIds.includes(original.purchaseOrderItemId);
        const isProcessingSuggestion =
          processingSuggestionItemId === original.purchaseOrderItemId;

        if (isQueuedMatch) {
          return <ProgressBadge label="Processing…" />;
        }

        if (
          purchaseOrderDetails?.status !== 'New' ||
          original.suggestionStatus !== 'Pending'
        ) {
          return null;
        }

        if (isProcessingSuggestion) {
          return <ProgressBadge label="Processing…" bg="info" />;
        }

        return (
          <div className="d-flex flex-column gap-1">
            <Badge bg="warning" text="dark" className="align-self-start">
              Suggested ({Math.round(original.suggestedMatchConfidence ?? 0)}%)
            </Badge>
            <span className="fs-10">
              {original.suggestedProductName ||
                `Product #${original.suggestedProductId}`}
            </span>
            <div className="d-flex gap-1">
              <Button
                size="sm"
                variant="success"
                disabled={processingSuggestionItemId != null}
                onClick={() =>
                  handleAcceptSuggestion(original.purchaseOrderItemId)
                }
              >
                Confirm
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={processingSuggestionItemId != null}
                onClick={() =>
                  handleRejectSuggestion(original.purchaseOrderItemId)
                }
              >
                Reject
              </Button>
            </div>
          </div>
        );
      }
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: 'Quantity',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-nowrap' },
        cellProps: { className: 'text-nowrap' }
      }
    },
    {
      id: 'estimatedUnitPrice',
      header: 'Est. Unit Price',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <EstimatedUnitPriceCell
          item={original}
          branchDomain={selectedBranch?.branchDomain}
          purchaseOrderId={purchaseOrderId}
          canEdit={canEditEstimate && !original.isCancelled}
          onUpdated={handleEstimatedPriceUpdated}
        />
      )
    },
    {
      id: 'estimatedAmount',
      header: 'Est. Amount',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <span>{formatPeso(original.estimatedAmount)}</span>
      )
    },
    {
      id: 'existingQty',
      header: 'Existing Qty',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const qty =
          original.quantityOnHand ?? original.existingQuantityOnHand ?? null;
        if (qty === null || qty === undefined) {
          return <span className="text-body-tertiary">—</span>;
        }
        return <span>{qty}</span>;
      }
    },
    {
      id: 'orderingOpinion',
      header: 'AI Opinion',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (isOrderingOpinionRunning && !original.orderingOpinion) {
          return <span className="fs-10 text-body-tertiary">Analyzing…</span>;
        }
        if (!original.orderingOpinion) {
          return <span className="text-body-tertiary">—</span>;
        }
        return (
          <OrderingOpinionBadge
            verdict={original.orderingOpinionVerdict}
            opinion={original.orderingOpinion}
            tooltipId={`po-opinion-${original.purchaseOrderItemId}`}
          />
        );
      }
    },
    {
      id: 'lastQuantityReceived',
      accessorKey: 'lastQuantityReceived',
      header: 'Last Qty Received',
      enableSorting: false
    },
    {
      id: 'lastDeliveryReceivedOn',
      accessorKey: 'lastDeliveryReceivedOn',
      header: 'Last Delivery Received',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <span>
            {original.lastDeliveryReceivedOn === null
              ? null
              : formatLongIsoDate(original.lastDeliveryReceivedOn as string)}
          </span>
        );
      }
    },
    {
      id: 'orderByUserName',
      accessorKey: 'orderByUserName',
      header: 'Ordered By',
      enableSorting: false,
      cell: ({ row: { original } }) => original.orderByUserName || 'System'
    },
    {
      id: 'remarks',
      header: 'Comment',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <RemarksCell
          item={original}
          branchDomain={selectedBranch?.branchDomain}
          purchaseOrderId={purchaseOrderId}
          canEdit={canEditRemarks && !original.isCancelled}
          onUpdated={handleEstimatedPriceUpdated}
        />
      )
    },
    {
      id: 'fulfillmentStatus',
      accessorKey: 'fulfillmentStatus',
      header: 'Fulfillment Status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (original.isNoStock) {
          return (
            <div className="d-flex flex-column gap-1">
              <BadgeStatus status="NoStock" />
              {original.movedToPurchaseOrderId ? (
                <Link
                  to={`/purchase-order-details/${original.movedToPurchaseOrderId}`}
                  className="fs-10"
                >
                  Moved to PO #{original.movedToPurchaseOrderId}
                </Link>
              ) : null}
            </div>
          );
        }
        if (original.isCancelled) {
          return (
            <div className="d-flex flex-column gap-1">
              <BadgeStatus status="Cancelled" />
              {original.movedToPurchaseOrderId ? (
                <Link
                  to={`/purchase-order-details/${original.movedToPurchaseOrderId}`}
                  className="fs-10"
                >
                  Moved to PO #{original.movedToPurchaseOrderId}
                </Link>
              ) : null}
            </div>
          );
        }
        const invoiceNumbers = original.purchaseOrderInvoiceNumber ?? [];
        return (
          <div className="d-flex flex-column gap-1">
            <BadgeStatus status={original.fulfillmentStatus} />
            {invoiceNumbers.map((invoiceNumber, index) => (
              <span
                key={`${invoiceNumber}-${index}`}
                className="fs-10 text-body-secondary"
              >
                {invoiceNumber}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: {
        headerProps: { className: 'text-end text-nowrap' },
        cellProps: { className: 'text-end text-nowrap' }
      },
      cell: ({ row: { original } }) => {
        const isActionableNoStock = isActionableNoStockItem(original);
        if (original.isCancelled && !isActionableNoStock) {
          return null;
        }

        const canActOnLine = original.fulfillmentStatus !== 'Fulfilled';
        const productLabel = original.productName || 'this item';
        const showMoveToSupplier =
          (isActionableNoStock && canRelocateNoStock) ||
          (!isActionableNoStock && canActOnLine && canMoveLine);
        const showMoveToResupply =
          (isActionableNoStock && canRelocateNoStock) ||
          (!isActionableNoStock && canActOnLine && canMoveToResupply);
        if (isActionableNoStock && !showMoveToSupplier && !showMoveToResupply) {
          return null;
        }

        return (
          <Dropdown
            onClick={event => event.stopPropagation()}
            align="end"
          >
            <Dropdown.Toggle
              variant="phoenix-secondary"
              size="sm"
              id={`po-item-actions-${original.purchaseOrderItemId}`}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: 'fixed' }}
            >
              {!isActionableNoStock && (
                <Dropdown.Item
                  onClick={() =>
                    editPurchaseOrderItem({
                      productId: original.productId,
                      productName: original.productName,
                      quantity: original.quantity,
                      unitOfMeasure: original.unitOfMeasure,
                      remarks: original.remarks,
                      purchaseOrderId: original.purchaseOrderId,
                      purchaseOrderItemId: original.purchaseOrderItemId
                    })
                  }
                >
                  Edit
                </Dropdown.Item>
              )}
              {!isActionableNoStock && (
                <Dropdown.Item
                  onClick={() =>
                    deletePurchaseOrderItem(
                      original.purchaseOrderItemId,
                      original.productName
                    )
                  }
                >
                  Delete
                </Dropdown.Item>
              )}
              {!isActionableNoStock && canActOnLine && canCancelLine && (
                <Dropdown.Item
                  onClick={() =>
                    cancelPurchaseOrderItem(
                      original.purchaseOrderItemId,
                      productLabel
                    )
                  }
                >
                  Cancel item
                </Dropdown.Item>
              )}
              {showMoveToSupplier && (
                <Dropdown.Item
                  onClick={() =>
                    movePurchaseOrderItem(
                      original.purchaseOrderItemId,
                      productLabel
                    )
                  }
                >
                  Move to another supplier
                </Dropdown.Item>
              )}
              {showMoveToResupply && (
                <Dropdown.Item
                  onClick={() =>
                    movePurchaseOrderItemToResupply(
                      original.purchaseOrderItemId,
                      productLabel
                    )
                  }
                >
                  Move to resupply
                </Dropdown.Item>
              )}
              {!isActionableNoStock && canActOnLine && canMarkNoStock && (
                <Dropdown.Item
                  onClick={() =>
                    markPurchaseOrderItemNoStock(
                      original.purchaseOrderItemId,
                      productLabel
                    )
                  }
                >
                  No stock
                </Dropdown.Item>
              )}
              {!isActionableNoStock &&
                canBindToInvoice &&
                original.productId != null && (
                <Dropdown.Item
                  onClick={() =>
                    bindPurchaseOrderItemToInvoice(
                      original.purchaseOrderItemId,
                      productLabel,
                      (original.purchaseOrderInvoiceNumber ?? []).length > 0
                    )
                  }
                >
                  {(original.purchaseOrderInvoiceNumber ?? []).length > 0
                    ? 'Change invoice'
                    : 'Bind to invoice'}
                </Dropdown.Item>
              )}
              {!isActionableNoStock &&
                canBindToInvoice &&
                (original.purchaseOrderInvoiceNumber ?? []).length > 0 && (
                  <Dropdown.Item
                    onClick={() =>
                      removePurchaseOrderItemInvoice(
                        original.purchaseOrderItemId,
                        productLabel
                      )
                    }
                  >
                    Remove invoice
                  </Dropdown.Item>
                )}
              {!isActionableNoStock && canMarkFulfilled && canActOnLine && (
                <Dropdown.Item
                  onClick={() =>
                    markPurchaseOrderItemFulfilled(
                      original.purchaseOrderItemId,
                      productLabel
                    )
                  }
                >
                  Mark as fulfilled
                </Dropdown.Item>
              )}
              {!isActionableNoStock && isAdmin && original.productId == null && (
                <Dropdown.Item
                  onClick={() => setCreateFromUnencodedItem(original)}
                >
                  Create product
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        );
      }
    }
  ];

  const visibleSet = new Set(visibleColumns);
  const columns = allColumns.filter(column => {
    const columnId = column.id;
    if (!columnId || ALWAYS_VISIBLE_COLUMN_IDS.has(columnId)) {
      return true;
    }
    return visibleSet.has(columnId as ConfigurableColumnId);
  });

  const hasActionableNoStock = purchaseOrderItems.some(isActionableNoStockItem);
  const showMoveSelected =
    canMoveLine || (hasActionableNoStock && canRelocateNoStock);
  const showMoveToResupplySelected =
    canMoveToResupply || (hasActionableNoStock && canRelocateNoStock);

  const isMovableItem = (item: TPurchaseOrderItem) => {
    if (isActionableNoStockItem(item)) {
      return canRelocateNoStock;
    }

    return !item.isCancelled && item.fulfillmentStatus !== 'Fulfilled';
  };

  const canSelectItems =
    showMoveSelected || showMoveToResupplySelected || canMarkNoStock;

  const table = useAdvanceTable({
    data: purchaseOrderItems,
    columns,
    pagination: false,
    selection: canSelectItems,
    enableRowSelection: row => isMovableItem(row.original),
    getRowId: row => String(row.purchaseOrderItemId)
  });

  const selectedMovableItems = table
    .getSelectedRowModel()
    .rows.map(row => row.original)
    .filter(isMovableItem);

  const confirmMoveSelected = () => {
    if (selectedMovableItems.length === 0) {
      toast.error('Select at least one item to move', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    modalRef.current.show();
    setModalProps({
      title: `Move ${selectedMovableItems.length} item${
        selectedMovableItems.length === 1 ? '' : 's'
      } to the next PO`,
      content: (
        <p className="mb-0">
          Move the selected items to the next purchase order for{' '}
          <strong>{purchaseOrderDetails?.supplierName}</strong>.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () =>
        handleMoveSelectedItems(
          selectedMovableItems.map(item => item.purchaseOrderItemId)
        ),
      onSubmitCustomDisplay: 'Move items'
    });
  };

  const confirmMoveSelectedToResupply = () => {
    if (selectedMovableItems.length === 0) {
      toast.error('Select at least one item to move to resupply', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    modalRef.current.show();
    setModalProps({
      title: `Move ${selectedMovableItems.length} item${
        selectedMovableItems.length === 1 ? '' : 's'
      } to resupply`,
      content: (
        <p className="mb-0">
          Move the selected items to the resupply list? Remaining quantity will
          be added there and these lines will be taken off the purchase order.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () =>
        handleMoveSelectedItemsToResupply(
          selectedMovableItems.map(item => item.purchaseOrderItemId)
        ),
      onSubmitCustomDisplay: 'Move to resupply'
    });
  };

  const confirmMarkSelectedNoStock = () => {
    if (selectedMovableItems.length === 0) {
      toast.error('Select at least one item to mark as no stock', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    modalRef.current.show();
    setModalProps({
      title: `No stock for ${selectedMovableItems.length} item${
        selectedMovableItems.length === 1 ? '' : 's'
      }`,
      content: (
        <>
          <p className="mb-3">
            Flag the selected items as no stock at the supplier? They stay on
            the purchase order history and remaining quantity is moved to
            resupply.
          </p>
          <CancelCommentForm
            key={`nostock-selected-${selectedMovableItems.length}`}
            ref={cancelCommentFormRef}
            label="Comment"
            placeholder="Optional note about the supplier stock"
          />
        </>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () =>
        handleMarkSelectedItemsNoStock(
          selectedMovableItems.map(item => item.purchaseOrderItemId)
        ),
      onSubmitCustomDisplay: 'No stock'
    });
  };

  useEffect(() => {
    fetchSupplierDetails();
  }, [debouncedBranchDomain, fetchSupplierDetails]);

  useEffect(() => {
    if (debouncedBranchDomain) {
      fetchPurchaseOrderItems();
    }
  }, [debouncedBranchDomain]);

  useEffect(() => {
    setMatchingItemIds(prev => {
      if (prev.length === 0) {
        return prev;
      }

      const next = prev.filter(id => {
        const item = purchaseOrderItems.find(
          row => row.purchaseOrderItemId === id
        );
        // Drop once the refreshed row is no longer queued (or missing from the page).
        return item != null && isProductMatchQueued(item.productMatchStatus);
      });

      return next.length === prev.length && next.every((id, i) => id === prev[i])
        ? prev
        : next;
    });
  }, [purchaseOrderItems]);

  useEffect(() => {
    const shouldPoll =
      (pollForMatchesUntil != null && Date.now() <= pollForMatchesUntil) ||
      hasImagesExtracting ||
      hasQueuedProductMatches ||
      isOrderingOpinionRunning ||
      purchaseOrderDetails?.hasBackgroundTaskInProgress === true;

    if (!shouldPoll) {
      if (pollForMatchesUntil && Date.now() > pollForMatchesUntil) {
        setPollForMatchesUntil(null);
      }
      return;
    }

    const intervalId = window.setInterval(() => {
      if (pollForMatchesUntil && Date.now() > pollForMatchesUntil) {
        setPollForMatchesUntil(null);
      }
      fetchPurchaseOrderItems();
      fetchSupplierDetails({ silent: true });
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [
    pollForMatchesUntil,
    hasImagesExtracting,
    hasQueuedProductMatches,
    isOrderingOpinionRunning,
    purchaseOrderDetails?.hasBackgroundTaskInProgress
  ]);

  return (
    <>
      <Row lg="auto" className="d-flex justify-content-between mb-3">
        <h4>Purchase Order Details</h4>
        <PurchaseOrderActions
          status={purchaseOrderDetails?.status as string}
          canApprove={canApprove}
          canDelete={canDelete}
          isAdmin={isAdmin}
          hasPendingSuggestions={hasPendingSuggestions}
          addPurchaseOrderItem={addPurchaseOrderItem}
          uploadListPhoto={uploadListPhoto}
          sendForApproval={sendForApproval}
          markAsNew={markAsNew}
          approveRequest={approveRequest}
          confirmRequest={confirmRequest}
          closeOrder={confirmCloseOrder}
          cancelOrder={cancelPurchaseOrder}
          requestForDeletion={requestForDeletion}
          deletePurchaseOrder={handleApproveRequestForDeletion}
          download={handleGeneratePdf}
          isQueueingAiJob={isQueueingAiJob}
          onRerunListExtraction={() =>
            queueAiJob(
              'rerunListExtraction',
              'List extraction queued — may add duplicate lines',
              'Re-run list extraction? Already-processed images will be reset and may create duplicate line items.'
            )
          }
          onRematchProducts={() =>
            queueAiJob('rematchProducts', 'Product rematch queued')
          }
          onRerunOrderingOpinions={() =>
            queueAiJob('rerunOrderingOpinions', 'Ordering opinions queued')
          }
        />
      </Row>
      {isAdmin && (
        <ProcurementLimitWarning
          branchDomain={selectedBranch?.branchDomain}
          variant="purchaseOrder"
        />
      )}
      <HtmlPdfPreviewModal
        show={showPdfPreview}
        onHide={closePdfPreview}
        title="Purchase Order PDF"
        preview={pdfPreview}
        isLoading={isGeneratingPdf}
      />
      <Card className="mb-5">
        <Card.Body>
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                  <div className="fs-10 text-body-tertiary text-uppercase mb-1">
                    Purchase order
                  </div>
                  <h4 className="mb-0">
                    #{purchaseOrderDetails?.purchaseOrderId}
                  </h4>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <BadgeStatus status={purchaseOrderDetails?.status} />
                  {purchaseOrderDetails?.needsConsolidation && (
                    <BadgeStatus status="ForConsolidation" />
                  )}
                  {hasBackgroundTaskInProgress && (
                    <ProgressBadge
                      label={isAnalyzing ? 'Analyzing…' : 'In progress'}
                      title={
                        isAnalyzing
                          ? 'AI ordering analysis in progress'
                          : 'Background task in progress'
                      }
                    />
                  )}
                </div>
              </div>
              <SupplierName
                supplierId={purchaseOrderDetails?.supplierId}
                supplierName={purchaseOrderDetails?.supplierName}
              />
              <div className="d-flex flex-wrap gap-4 mt-3">
                <div>
                  <div className="fs-10 text-body-tertiary text-uppercase mb-1">
                    Created
                  </div>
                  <div>
                    {formatLongIsoDateOrDash(purchaseOrderDetails?.createdOn)}
                  </div>
                </div>
                <div>
                  <div className="fs-10 text-body-tertiary text-uppercase mb-1">
                    Sent
                  </div>
                  <div>
                    {formatLongIsoDateOrDash(purchaseOrderDetails?.sentOn)}
                  </div>
                </div>
                <div>
                  <div className="fs-10 text-body-tertiary text-uppercase mb-1">
                    Closed
                  </div>
                  <div>
                    {formatLongIsoDateOrDash(purchaseOrderDetails?.closedOn)}
                  </div>
                </div>
                {purchaseOrderDetails?.cancelledOn && (
                  <div>
                    <div className="fs-10 text-body-tertiary text-uppercase mb-1">
                      Cancelled
                    </div>
                    <div>
                      {formatLongIsoDateOrDash(
                        purchaseOrderDetails.cancelledOn
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <span className="text-body-tertiary">
                  Estimated procurement amount:{' '}
                </span>
                <span className="fw-semibold">
                  {formatPeso(purchaseOrderDetails?.estimatedProcurementAmount)}
                </span>
                {purchaseOrderDetails?.hasIncompleteEstimate && (
                  <span className="text-body-tertiary fs-9 ms-2">
                    Some lines have no price yet
                  </span>
                )}
              </div>
              {(hasImagesExtracting ||
                hasQueuedProductMatches ||
                isOrderingOpinionRunning) && (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {hasImagesExtracting && (
                    <Badge bg="warning" variant="phoenix" className="fs-10">
                      Extracting list items…
                    </Badge>
                  )}
                  {hasQueuedProductMatches && (
                    <ProgressBadge label="Matching products…" />
                  )}
                  {(isAnalyzing || isOrderingOpinionRunning) && (
                    <ProgressBadge
                      label="Running AI ordering analysis…"
                      bg="info"
                    />
                  )}
                </div>
              )}
              {(purchaseOrderDetails?.images?.length ?? 0) > 0 && (
                <div className="mt-3">
                  <div className="fw-semibold mb-2">List photos</div>
                  <div className="d-flex flex-wrap gap-2">
                    {purchaseOrderDetails?.images?.map(image => (
                      <div
                        key={image.purchaseOrderImageId}
                        className="d-flex align-items-center gap-1"
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="d-flex align-items-center gap-2"
                          onClick={() =>
                            handlePreviewListImage(image.googleFileId)
                          }
                          title={image.extractionError || image.filename}
                        >
                          <span className="text-truncate" style={{ maxWidth: 140 }}>
                            {image.filename}
                          </span>
                          <Badge bg={getExtractionBadgeBg(image.extractionStatus)}>
                            {getExtractionStatusLabel(image.extractionStatus)}
                          </Badge>
                        </Button>
                        {purchaseOrderDetails?.status === 'New' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteListImage(image)}
                            title="Delete list photo"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {showMoveSelected && (
            <Button
              variant="phoenix-primary"
              size="sm"
              disabled={selectedMovableItems.length === 0}
              onClick={confirmMoveSelected}
            >
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="me-2" />
              Move selected to next PO
              {selectedMovableItems.length > 0
                ? ` (${selectedMovableItems.length})`
                : ''}
            </Button>
          )}
          {showMoveToResupplySelected && (
            <Button
              variant="phoenix-secondary"
              size="sm"
              disabled={selectedMovableItems.length === 0}
              onClick={confirmMoveSelectedToResupply}
            >
              <FontAwesomeIcon icon={faBoxesStacked} className="me-2" />
              Move selected to resupply
              {selectedMovableItems.length > 0
                ? ` (${selectedMovableItems.length})`
                : ''}
            </Button>
          )}
          {canMarkNoStock && (
            <Button
              variant="phoenix-danger"
              size="sm"
              disabled={selectedMovableItems.length === 0}
              onClick={confirmMarkSelectedNoStock}
            >
              <FontAwesomeIcon icon={faBan} className="me-2" />
              No stock
              {selectedMovableItems.length > 0
                ? ` (${selectedMovableItems.length})`
                : ''}
            </Button>
          )}
        </div>
        <Dropdown align="end" autoClose="outside">
          <Dropdown.Toggle
            variant="phoenix-secondary"
            className="d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faColumns} />
            Columns
          </Dropdown.Toggle>
          <Dropdown.Menu className="px-3 py-2" style={{ minWidth: '14rem' }}>
            <div className="fw-semibold mb-2">Show columns</div>
            {CONFIGURABLE_COLUMNS.map(column => (
              <Form.Check
                key={column.id}
                type="checkbox"
                id={`purchase-order-item-column-${column.id}`}
                className="mb-1"
                label={column.label}
                checked={visibleColumns.includes(column.id)}
                onChange={() => toggleColumnVisibility(column.id)}
              />
            ))}
            <Dropdown.Divider />
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={resetColumnVisibility}
            >
              Reset to default
            </Button>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{
            size: 'lg',
            className:
              'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
          }}
          rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          bodyClassName="py-3"
        />
      </AdvanceTableProvider>

      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        onSubmitCustomDisplay={modalProps?.onSubmitCustomDisplay}
      />
      {createFromUnencodedItem != null && (
        <CreateProductFromUnencodedModal
          key={createFromUnencodedItem.purchaseOrderItemId}
          show
          handleClose={() => setCreateFromUnencodedItem(null)}
          query={
            createFromUnencodedItem.productName ||
            `PO line ${createFromUnencodedItem.purchaseOrderItemId}`
          }
          sourceLabel="Purchase order line"
          onBind={bindCreatedProductToPurchaseOrderItem}
        />
      )}
      <ImageModal
        ref={imageModalRef}
        image={imageModalProps?.image}
        size={imageModalProps?.size}
        googleFileId={imageModalProps?.googleFileId}
        caption={imageModalProps?.caption}
      />
    </>
  );
};

export default PurchaseOrderDetails;
